import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
// Warms a route's lazy chunk on touch-down. There is no hover on mobile, but
// the gap between touch-down and click still covers most of the download.
import { prefetchHandlers } from "../lib/routePrefetch";
import {
  LayoutDashboard,
  Zap,
  Link2,
  BarChart3,
  MoreHorizontal,
  MessageSquare,
  Sparkles,
  Image as ImageIcon,
  Settings,
  Users,
  UserSquare2,
  Wand2,
  Brain,
  Handshake,
  Bell,
  X,
  Package,
  CreditCard,
} from "lucide-react";

const mainNav = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Home" },
  { path: "/automation", icon: Zap, label: "Automation" },
  { path: "/bio", icon: Link2, label: "Bio" },
  { path: "/analytics", icon: BarChart3, label: "Stats" },
];

import { isComingSoon } from "../config/comingSoon";

const moreItems = [
  { path: "/contacts", icon: UserSquare2, label: "Contacts", isNew: true },
  { path: "/products", icon: Package, label: "Products", isNew: true },
  { path: "/posts", icon: ImageIcon, label: "Posts" },
  { path: "/leads", icon: Users, label: "Leads" },
  { path: "/collabs", icon: Handshake, label: "Marketplace", isNew: true },
  { path: "/trends", icon: Sparkles, label: "Trends" },
  { path: "/ai-writer", icon: Wand2, label: "AI Writer" },
  { path: "/growth-agent", icon: Brain, label: "Agent" },
  { path: "/notifications", icon: Bell, label: "Notifications" },
  // Aug 2026 — the desktop sidebar has always had a Subscription entry in
  // its bottom section; the phone had no route to /pricing at all except
  // by hitting a paid lock. So the one page that answers "what does this
  // cost and what do I get" was reachable on a laptop and not on a phone,
  // which is the device most of these creators are on (P, 2026-08-22).
  //
  // Named "Pricing" rather than "Subscription": the desktop label is
  // paired with a plan badge that makes it read as account state, and
  // there is no room for that badge in a 4-across grid.
  { path: "/pricing", icon: CreditCard, label: "Pricing" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export default function MobileNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);

  const isActive = (path) =>
    location.pathname === path || (path === "/automation" && location.pathname.startsWith("/automation"));

  return (
    <>
      {/* More menu overlay */}
      <AnimatePresence>
        {showMore && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={() => setShowMore(false)}>
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="absolute bottom-0 left-0 right-0 bg-background border-t border-border rounded-t-2xl p-4 pb-safe" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold">More</span>
                <button onClick={() => setShowMore(false)} aria-label="Close" className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground active:scale-[0.94] transition-[background,color,transform] duration-150"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {moreItems.map(item => (
                  isComingSoon(item.path) ? (
                    // Same rule as the desktop sidebar, read from the same
                    // list: not tappable, and labelled so the entry reads
                    // as "not ready" rather than "broken".
                    <div key={item.path}
                      className="relative flex flex-col items-center gap-1.5 p-3 rounded-xl opacity-55 cursor-default select-none text-muted-foreground"
                      aria-disabled="true"
                      data-testid={`more-${item.label.toLowerCase().replace(/\s/g, "-")}-locked`}>
                      <span className="absolute top-1 right-1 px-1 py-0 text-[8px] font-bold bg-secondary text-muted-foreground rounded-full leading-tight">SOON</span>
                      <item.icon className="w-5 h-5" />
                      <span className="text-[10px] font-medium">{item.label}</span>
                    </div>
                  ) : (
                  <button key={item.path} onClick={() => { navigate(item.path); setShowMore(false); }}
                    {...prefetchHandlers(item.path)}
                    className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl transition-colors ${isActive(item.path) ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                    data-testid={`more-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
                    {item.isNew && (
                      <span className="absolute top-1 right-1 px-1 py-0 text-[8px] font-bold bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-full leading-tight">NEW</span>
                    )}
                    <item.icon className="w-5 h-5" />
                    <span className="text-[10px] font-medium">{item.label}</span>
                  </button>
                  )
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom nav bar */}
      <nav className="bg-background/90 backdrop-blur-xl border-t border-border safe-bottom" data-testid="mobile-nav">
        <div className="flex items-center justify-around px-1 py-1.5">
          {mainNav.map(item => {
            const active = isActive(item.path);
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                {...prefetchHandlers(item.path)}
                className="relative flex flex-col items-center gap-0.5 py-1.5 px-3 min-w-[56px]"
                data-testid={`nav-${item.label.toLowerCase()}`}>
                {active && (
                  <motion.div layoutId="mob-nav-active" className="absolute inset-0 bg-primary/10 rounded-xl"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }} />
                )}
                <item.icon className={`relative z-10 w-5 h-5 transition-colors ${active ? "text-primary" : "text-muted-foreground"}`} />
                <span className={`relative z-10 text-[10px] font-medium transition-colors ${active ? "text-primary" : "text-muted-foreground"}`}>{item.label}</span>
              </button>
            );
          })}
          {/* More button */}
          <button onClick={() => setShowMore(!showMore)}
            className={`relative flex flex-col items-center gap-0.5 py-1.5 px-3 min-w-[56px] ${showMore ? "text-primary" : "text-muted-foreground"}`}
            data-testid="nav-more">
            <MoreHorizontal className="w-5 h-5" />
            <span className="text-[10px] font-medium">More</span>
          </button>
        </div>
      </nav>
    </>
  );
}
